/**
 * The bell in the header — Max, 2026-10-14: "làm cho tôi tính năng notification nhé ... cho icon là
 * cái chuông nha".
 *
 * WHAT IT SHOWS is what the portal has written to this person: every mail sent to them (To or CC) by
 * any screen — a ticket, an approval asked or decided, a quote, a SKU dropped, a cash report waiting —
 * recorded at the send (portalNotify.js on the server). So it says the same thing the inbox does,
 * where the work is, with a link straight to the screen the mail's button opens.
 *
 * THE COUNT IS POLLED, quietly. Once a minute while the tab is visible, and again when it comes back
 * to the front; the request is marked passive so it never extends the session — a tab left open
 * still signs out after two idle hours, as it always has. The list itself is fetched only when the
 * bell is opened.
 *
 * WAITING FOR YOU — Jay, 2026-10-28, for the CEO: "The notification bell in the Portal must show a count of items
 * waiting for his approval … He should never have to go looking for what is waiting on him." Not a message but a live
 * count from the server (project approvals + date changes, projectChange.js), on top of the panel and in the badge.
 * Reading notifications does not clear it; deciding does. Everybody it does not concern gets null and sees nothing.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, CheckCheck, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { BACKEND_URL } from './backendConfig';
import { fmtDateTimeVN } from './timeZone';

const API = `${BACKEND_URL}/api/notifications`;
const POLL_MS = 60000;
const PASSIVE = { 'X-Portal-Passive': '1' };

/* LESS ON EACH ROW — Jay, 2026-10-16: "I think it contains too many information. Please reduce". A row
   printed the whole mail subject, the first 160 characters of the mail body and the module's name, so
   four notifications filled the panel. The subject already says what it is in a prefix — "[Ticket
   Reminder] — …" from the mails, "Sample overdue by 25 days: …" from the reminders — so that prefix
   becomes a small label and the rest is the line read, at most two lines. The body preview and the
   source are dropped from the row; the whole subject and the preview stay on the hover. */
const splitTitle = (title) => {
  const s = String(title || '').trim();
  let m = s.match(/^\[([^\]]{1,40})\]\s*(?:[—–-]\s*)?(.+)$/);
  if (m) return { kind: m[1].trim(), rest: m[2].trim() };
  m = s.match(/^((?:Ticket|Sample)\s[^:]{1,40}):\s*(.+)$/);
  if (m) return { kind: m[1].trim(), rest: m[2].trim() };
  return { kind: '', rest: s };
};
const kindTone = (kind) => (/overdue/i.test(kind) ? ' late' : (/due (today|tomorrow|in)/i.test(kind) ? ' soon' : ''));

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [waiting, setWaiting] = useState(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  /* THE GROUPS (2026-10-25, Jay: "too many notification ends up with reading nothing … For me, please don't send
     notification for Tickets"): the gear swaps the list for one tick per group. Off = hidden from this bell and its
     count; the mails are not touched. The server sorts each notification into its group (portalNotify.js). */
  const [prefs, setPrefs] = useState(null); // null = the list; [] … = the settings, being edited
  const [muted, setMuted] = useState([]);
  /* task #1194 (Jay): older notifications page in with "Load more" (`before` = the last page's oldest row), and the muted groups
     can be shown ("See all") — nothing the badge counts is out of reach */
  const [more, setMore] = useState({ hasMore: false, before: null, busy: false });
  const [seeAll, setSeeAll] = useState(false);
  const [prefBusy, setPrefBusy] = useState(false);
  /* GROUP CHAT, ROOM BY ROOM (Max 02/11): "a bell for every message" is each person's own choice per room, so it lives here
     and not in the room's header, where it read as the whole group's. [] for whoever has no GCC (the call answers 403). */
  const [gccRooms, setGccRooms] = useState([]);
  const gccWas = useRef(new Map());
  const wrap = useRef(null);

  const refreshCount = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const r = await fetch(`${API}/count`, { credentials: 'include', headers: PASSIVE });
      if (r.ok) { const d = await r.json(); setUnread(d.unread || 0); setWaiting(d.waiting || null); }
    } catch (e) { /* the next tick tries again; a bell is not worth an error on the page */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refreshCount(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [refreshCount]);

  const loadList = useCallback(async (opts = {}) => {
    setError('');
    const append = !!opts.before;
    if (append) setMore((m) => ({ ...m, busy: true }));
    try {
      const q = new URLSearchParams({ limit: '40' });
      if (opts.before) q.set('before', String(opts.before));
      if (opts.all) q.set('all', '1');
      const r = await fetch(`${API}?${q}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || i18n.t('xShell.failedStatus', { status: r.status }));
      setItems((list) => (append ? [...(list || []), ...(d.items || [])] : (d.items || [])));
      setMore({ hasMore: !!d.hasMore, before: d.before || null, busy: false });
      setUnread(d.unread || 0);
      setWaiting(d.waiting || null);
      setMuted(d.muted || []);
    } catch (e) { setError(e.message); if (!append) setItems([]); setMore((m) => ({ ...m, busy: false })); }
  }, []);

  const openPrefs = async () => {
    setError('');
    setGccRooms([]); // last time's rows would take a tick that the answer below then overwrites
    try {
      const r = await fetch(`${API}/prefs`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || i18n.t('xShell.failedStatus', { status: r.status }));
      setPrefs(d.groups || []);
    } catch (e) { setError(e.message); }
    try {
      const r = await fetch(`${BACKEND_URL}/api/gcc/bell`, { credentials: 'include' });
      const d = r.ok ? await r.json() : { rooms: [] };
      gccWas.current = new Map((d.rooms || []).map((x) => [x.roomPk, !!x.bellAll]));
      setGccRooms((d.rooms || []).map((x) => ({ ...x, bellAll: !!x.bellAll })));
    } catch (e) { setGccRooms([]); }
  };
  const savePrefs = async () => {
    setPrefBusy(true);
    try {
      const r = await fetch(`${API}/prefs`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ muted: prefs.filter((g) => !g.on).map((g) => g.key) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || i18n.t('xShell.failedStatus', { status: r.status }));
      for (const x of gccRooms.filter((g) => gccWas.current.get(g.roomPk) !== g.bellAll)) {
        // eslint-disable-next-line no-await-in-loop
        const q = await fetch(`${BACKEND_URL}/api/gcc/rooms/${x.roomPk}/bell`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ on: x.bellAll }),
        });
        if (!q.ok) throw new Error((await q.json().catch(() => ({}))).error || i18n.t('xShell.failedStatus', { status: q.status }));
      }
      setUnread(d.unread || 0);
      setPrefs(null);
      setItems(null);
      loadList();
    } catch (e) { setError(e.message); }
    setPrefBusy(false);
  };

  useEffect(() => {
    if (!open) { setPrefs(null); setSeeAll(false); return undefined; }
    loadList();
    const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, loadList]);

  const markRead = async (body) => {
    try {
      const r = await fetch(`${API}/read`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok) setUnread(d.unread || 0);
    } catch (e) { /* the row stays bold; nothing else depends on it */ }
  };

  // a merged entry (task #1194) is read as a whole: every row it stands for
  const idsOf = (n) => (Array.isArray(n.ids) && n.ids.length ? n.ids : [n.id]);
  const openItem = (n) => {
    if (!n.read) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      markRead({ ids: idsOf(n) });
    }
    if (n.link) {
      setOpen(false);
      // A page of the app is a route; a file the server serves (/api/…) is a real navigation.
      if (n.link.startsWith('/api/')) window.location.assign(n.link);
      else navigate(n.link);
    }
  };

  /* ONE ROW, WITHOUT OPENING IT — Jay, 2026-10-16: "Please make a button on each article to click to
     'mark as read'". Opening a row already marks it read, but that also leaves the page somebody is on;
     this is for a notice that was read in the inbox and only needs clearing here. */
  const markOne = (n) => {
    setItems((list) => (list || []).map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    markRead({ ids: idsOf(n) });
  };

  const markAll = () => {
    setItems((list) => (list || []).map((x) => ({ ...x, read: true })));
    markRead({ all: true });
  };

  const waitingN = waiting?.count || 0;
  const total = unread + waitingN;
  const badge = total > 99 ? '99+' : String(total);
  const bellLabel = [unread ? t('notif.unreadCount', { count: unread }) : '', waitingN ? t('xDates.waitingYou', { n: waitingN }) : '']
    .filter(Boolean).join(' · ') || t('notif.title');
  return (
    <div className="nb-wrap" ref={wrap}>
      <button
        type="button"
        className={`nb-bell${total ? ' has-unread' : ''}`}
        title={bellLabel}
        aria-label={bellLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={16} strokeWidth={2} />
        {/* The inner span carries the text trim: text-box applies to a block container, and the
            badge itself is a flex container. */}
        {total > 0 && <span className="nb-badge" data-nb-waiting={waitingN || undefined}><span>{badge}</span></span>}
      </button>
      {open && (
        <div className="nb-panel" role="dialog" aria-label={t('notif.title')}>
          <div className="nb-head">
            <strong>{prefs ? t('notif.settings') : t('notif.title')}</strong>
            {!prefs && (
              <span className="nb-headbtns">
                <button type="button" className="nb-markall" onClick={markAll} disabled={!unread}>
                  <CheckCheck size={13} /> {t('notif.markAll')}
                </button>
                <button type="button" className={`nb-gear${muted.length ? ' on' : ''}`} onClick={openPrefs}
                  title={muted.length ? `${t('notif.settings')} · ${t('notif.hidden', { count: muted.length })}` : t('notif.settings')}
                  aria-label={t('notif.settings')}>
                  <Settings size={14} />
                </button>
              </span>
            )}
          </div>
          {prefs ? (
            <div className="nb-prefs">
              <p className="nb-prefs-hint">{t('notif.settingsHint')}</p>
              {error && <div className="nb-empty nb-err">{error}</div>}
              {prefs.map((g) => (
                <label key={g.key} className="nb-pref">
                  <input type="checkbox" checked={g.on}
                    onChange={(e) => setPrefs((list) => list.map((x) => (x.key === g.key ? { ...x, on: e.target.checked } : x)))} />
                  <span className="nb-pref-name">{t(`notif.g_${g.key}`)}</span>
                  {g.unread > 0 && <span className="nb-pref-n">{t('notif.unreadIn', { count: g.unread })}</span>}
                </label>
              )).flatMap((row, i) => (prefs[i].key === 'chat' && gccRooms.length ? [row, (
                <div key="gcc-rooms" className={`nb-subprefs${prefs[i].on ? '' : ' off'}`} data-nb-gccrooms>
                  <div className="nb-subprefs-h">{t('xGcc.bellRoomsHead')}</div>
                  {gccRooms.map((x) => (
                    <label key={x.roomPk} className="nb-pref nb-subpref">
                      <input type="checkbox" checked={x.bellAll} disabled={!prefs[i].on} data-nb-gccroom={x.roomPk}
                        onChange={(e) => setGccRooms((list) => list.map((y) => (y.roomPk === x.roomPk ? { ...y, bellAll: e.target.checked } : y)))} />
                      <span className="nb-pref-name">{x.name}</span>
                    </label>
                  ))}
                  <p className="nb-subprefs-note">{t('xGcc.bellRoomsNote')}</p>
                </div>
              )] : [row]))}
              <div className="nb-prefs-foot">
                <button type="button" className="nb-markall" onClick={() => setPrefs(null)}>{t('notif.cancel')}</button>
                <button type="button" className="nb-save" onClick={savePrefs} disabled={prefBusy}>{t('notif.save')}</button>
              </div>
            </div>
          ) : (
          <>
          {waitingN > 0 && (
            <button type="button" className="nb-waiting" data-nb-waiting-row="1"
              onClick={() => { setOpen(false); navigate(waiting.link || '/development/project-approval?tab=pending'); }}>
              <strong>{t('xDates.waitingYou', { n: waitingN })}</strong>
              <span>{[waiting.projects ? t('xDates.waitingProjects', { n: waiting.projects }) : '',
                waiting.changes ? t('xDates.waitingChanges', { n: waiting.changes }) : ''].filter(Boolean).join(' · ')}</span>
            </button>
          )}
          {muted.length > 0 && (
            <div className="nb-hidden-note">
              {t('notif.hidden', { count: muted.length })}
              {' · '}
              <button type="button" className="nb-linkbtn" data-nb-seeall={seeAll ? 'on' : 'off'}
                onClick={() => { const next = !seeAll; setSeeAll(next); setItems(null); loadList({ all: next }); }}>
                {seeAll ? t('notif.hideHidden') : t('notif.showHidden')}
              </button>
            </div>
          )}
          <div className="nb-list">
            {error && <div className="nb-empty nb-err">{error}</div>}
            {!error && items === null && <div className="nb-empty">{t('notif.loading')}</div>}
            {!error && items && !items.length && <div className="nb-empty">{t('notif.empty')}</div>}
            {(items || []).map((n) => {
              const { kind, rest: own } = splitTitle(n.title);
              const who = (n.actors || []).join(', ');
              // several updates of one task in a row, merged by the server (task #1194): one line, the latest change on hover
              const rest = n.count > 1 && n.subject
                ? (who ? t('notif.merged', { who, count: n.count, subject: n.subject }) : t('notif.mergedNoWho', { count: n.count, subject: n.subject }))
                : own;
              const hover = [n.title, n.preview].filter(Boolean).join('\n\n');
              /* Two buttons side by side, not one inside the other — a button cannot hold a button. */
              return (
                <div key={n.id} className={`nb-item${n.read ? '' : ' unread'}${n.link ? '' : ' nolink'}`}>
                  <button type="button" className="nb-main" onClick={() => openItem(n)} title={hover}>
                    <span className="nb-dot" aria-hidden="true" />
                    <span className="nb-body">
                      <span className="nb-line1">
                        {kind && <span className={`nb-kind${kindTone(kind)}`}>{kind}</span>}
                        <span className="nb-meta">
                          {[n.actor ? t('notif.by', { name: n.actor }) : '', fmtDateTimeVN(n.at), n.muted ? t('notif.fromHidden') : ''].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <span className="nb-title">{rest}</span>
                    </span>
                  </button>
                  {!n.read && (
                    <button
                      type="button"
                      className="nb-read"
                      onClick={() => markOne(n)}
                      title={t('notif.markRead')}
                      aria-label={t('notif.markRead')}
                    >
                      <Check size={14} strokeWidth={2.4} />
                    </button>
                  )}
                </div>
              );
            })}
            {more.hasMore && items && items.length > 0 && (
              <button type="button" className="nb-more" disabled={more.busy} data-nb-more
                onClick={() => loadList({ before: more.before, all: seeAll })}>
                {more.busy ? t('notif.loading') : t('notif.loadMore')}
              </button>
            )}
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
}
